import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { PageHeader, FormGroup, Input, Loading } from '../components/UI';
import api from '../services/api';

const commApi = {
  getConfig: () => api.get('/communications/whatsapp/config'),
  saveConfig: (data) => api.post('/communications/whatsapp/config', data),
  getQRCode: () => api.get('/communications/whatsapp/qrcode'),
  getStatus: () => api.get('/communications/whatsapp/status'),
  sendWhatsApp: (data) => api.post('/communications/whatsapp/send', data),
  sendEmail: (data) => api.post('/communications/email/send', data),
  runAlerts: () => api.post('/communications/alerts/run'),
};

export default function CommunicationsPage() {
  const [config, setConfig] = useState({ evolution_url: '', evolution_key: '', whatsapp_number: '' });
  const [status, setStatus] = useState({ connected: false, state: '' });
  const [qrcode, setQrcode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alertRunning, setAlertRunning] = useState(false);
  const [alertResult, setAlertResult] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [testMsg, setTestMsg] = useState('Olá! Esta é uma mensagem de teste do SaniTech. 🚽');
  const [tab, setTab] = useState('whatsapp');

  useEffect(() => {
    loadConfig();
    loadStatus();
  }, []);

  const loadConfig = async () => {
    try {
      const r = await commApi.getConfig();
      setConfig(c => ({ ...c, ...r.data }));
    } catch {} finally { setLoading(false); }
  };

  const loadStatus = async () => {
    try {
      const r = await commApi.getStatus();
      setStatus(r.data);
    } catch {}
  };

  const saveConfig = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await commApi.saveConfig(config);
      toast.success('Configuração salva! Escaneie o QR Code para conectar.');
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const loadQRCode = async () => {
    try {
      const r = await commApi.getQRCode();
      const qr = r.data?.base64 || r.data?.qrcode?.base64 || r.data?.code;
      setQrcode(qr);
      if (!qr) toast.error('QR Code não disponível. Verifique a instância na Evolution API.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao obter QR Code');
    }
  };

  const sendTest = async () => {
    if (!testPhone) return toast.error('Digite um número');
    try {
      await commApi.sendWhatsApp({ phone: testPhone, message: testMsg });
      toast.success('Mensagem enviada!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar. Verifique se o WhatsApp está conectado.');
    }
  };

  const runAlerts = async () => {
    setAlertRunning(true);
    try {
      const r = await commApi.runAlerts();
      setAlertResult(r.data);
      toast.success(`Alertas enviados! ${r.data.notifications_sent} notificações.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao executar alertas');
    } finally { setAlertRunning(false); }
  };

  if (loading) return <Loading />;

  const tabs = [
    { key: 'whatsapp', label: '💬 WhatsApp' },
    { key: 'alerts', label: '🔔 Alertas Automáticos' },
    { key: 'smtp', label: '📧 E-mail (SMTP)' },
  ];

  return (
    <div className="page">
      <PageHeader
        title="Comunicações"
        subtitle="WhatsApp, e-mail e alertas automáticos"
      />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: '1.5rem', borderBottom: '2px solid var(--border)' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '.75rem 1.25rem', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: tab === t.key ? 700 : 400,
            color: tab === t.key ? 'var(--primary)' : 'var(--text-muted)',
            borderBottom: tab === t.key ? '2px solid var(--primary)' : '2px solid transparent',
            marginBottom: -2, fontSize: '.9rem',
          }}>{t.label}</button>
        ))}
      </div>

      {/* WhatsApp tab */}
      {tab === 'whatsapp' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
          {/* Config */}
          <div className="card" style={{ padding: '1.5rem' }}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600 }}>
              Configuração Evolution API
            </h3>

            <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', marginBottom: '1rem', padding: '.75rem', background: status.connected ? '#dcfce7' : '#fef9c3', borderRadius: 8 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: status.connected ? '#16a34a' : '#f59e0b' }} />
              <span style={{ fontSize: '.875rem', fontWeight: 600, color: status.connected ? '#15803d' : '#92400e' }}>
                {status.connected ? '✅ WhatsApp Conectado' : `⚠️ ${status.state || 'Não conectado'}`}
              </span>
              <button className="btn btn-sm btn-secondary" onClick={loadStatus} style={{ marginLeft: 'auto' }}>↻</button>
            </div>

            <form onSubmit={saveConfig}>
              <FormGroup label="URL da Evolution API" required>
                <Input
                  value={config.evolution_url}
                  onChange={e => setConfig(c => ({ ...c, evolution_url: e.target.value }))}
                  placeholder="https://sua-evolution-api.com"
                  required
                />
              </FormGroup>
              <FormGroup label="API Key" required>
                <Input
                  type="password"
                  value={config.evolution_key}
                  onChange={e => setConfig(c => ({ ...c, evolution_key: e.target.value }))}
                  placeholder="sua-api-key"
                  required
                />
              </FormGroup>
              <FormGroup label="Número WhatsApp (com DDD, sem +55)">
                <Input
                  value={config.whatsapp_number}
                  onChange={e => setConfig(c => ({ ...c, whatsapp_number: e.target.value }))}
                  placeholder="11999999999"
                />
              </FormGroup>
              <div style={{ display: 'flex', gap: '.5rem' }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Salvando...' : 'Salvar e Conectar'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={loadQRCode}>
                  📷 Ver QR Code
                </button>
              </div>
            </form>

            {qrcode && (
              <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                <p style={{ fontSize: '.85rem', color: 'var(--text-muted)', marginBottom: '.5rem' }}>
                  Escaneie com o WhatsApp do número configurado:
                </p>
                <img
                  src={qrcode.startsWith('data:') ? qrcode : `data:image/png;base64,${qrcode}`}
                  alt="QR Code WhatsApp"
                  style={{ width: 200, height: 200, border: '1px solid var(--border)', borderRadius: 8 }}
                />
              </div>
            )}
          </div>

          {/* Test */}
          <div className="card" style={{ padding: '1.5rem' }}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600 }}>
              Enviar Mensagem de Teste
            </h3>
            <FormGroup label="Número destino (com DDD)">
              <Input
                value={testPhone}
                onChange={e => setTestPhone(e.target.value)}
                placeholder="11999999999"
              />
            </FormGroup>
            <FormGroup label="Mensagem">
              <textarea
                className="input"
                rows={4}
                value={testMsg}
                onChange={e => setTestMsg(e.target.value)}
                style={{ resize: 'vertical' }}
              />
            </FormGroup>
            <button className="btn btn-primary" onClick={sendTest} style={{ width: '100%', justifyContent: 'center' }}>
              💬 Enviar WhatsApp
            </button>

            <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'var(--bg-secondary)', borderRadius: 8 }}>
              <p style={{ fontSize: '.8rem', fontWeight: 600, color: 'var(--text-muted)', margin: '0 0 .5rem' }}>
                COMO CONFIGURAR A EVOLUTION API
              </p>
              <ol style={{ fontSize: '.82rem', color: 'var(--text-secondary)', paddingLeft: '1.2rem', margin: 0, lineHeight: 1.7 }}>
                <li>Instale a Evolution API na sua VPS:<br /><code style={{ fontSize: '.78rem' }}>docker run -d evolution-api</code></li>
                <li>Acesse o painel e gere uma API Key</li>
                <li>Configure a URL e Key acima</li>
                <li>Escaneie o QR Code com o WhatsApp</li>
                <li>Pronto! Envie a mensagem de teste</li>
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* Alerts tab */}
      {tab === 'alerts' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
          <div className="card" style={{ padding: '1.5rem' }}>
            <h3 style={{ margin: '0 0 .5rem', fontSize: '1rem', fontWeight: 600 }}>Alertas Automáticos</h3>
            <p style={{ fontSize: '.875rem', color: 'var(--text-muted)', marginBottom: '1.25rem' }}>
              Dispara notificações para clientes com contratos vencendo nos próximos 7 dias e cobranças em atraso.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem', marginBottom: '1.25rem' }}>
              {[
                { icon: '📄', title: 'Contratos vencendo', desc: 'Avisa clientes com contratos expirando em 7 dias' },
                { icon: '💰', title: 'Cobranças em atraso', desc: 'Notifica clientes com pagamentos vencidos' },
              ].map(a => (
                <div key={a.title} style={{ display: 'flex', gap: '1rem', padding: '.75rem', background: 'var(--bg-secondary)', borderRadius: 8 }}>
                  <span style={{ fontSize: '1.5rem' }}>{a.icon}</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '.9rem' }}>{a.title}</div>
                    <div style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>{a.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <button
              className="btn btn-primary"
              onClick={runAlerts}
              disabled={alertRunning}
              style={{ width: '100%', justifyContent: 'center' }}
            >
              {alertRunning ? '⏳ Enviando alertas...' : '🔔 Executar Alertas Agora'}
            </button>

            {alertResult && (
              <div style={{ marginTop: '1rem', padding: '1rem', background: '#dcfce7', borderRadius: 8 }}>
                <p style={{ fontWeight: 600, color: '#15803d', margin: '0 0 .5rem' }}>✅ Alertas concluídos!</p>
                <div style={{ fontSize: '.875rem', color: '#166534' }}>
                  <div>📄 Contratos vencendo: <strong>{alertResult.contracts_expiring}</strong></div>
                  <div>💰 Cobranças em atraso: <strong>{alertResult.financial_overdue}</strong></div>
                  <div>📨 Notificações enviadas: <strong>{alertResult.notifications_sent}</strong></div>
                </div>
              </div>
            )}
          </div>

          <div className="card" style={{ padding: '1.5rem' }}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 600 }}>Agendamento Automático</h3>
            <p style={{ fontSize: '.875rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
              Para executar os alertas automaticamente todo dia, adicione um cron job na VPS:
            </p>
            <div style={{ background: '#1e293b', borderRadius: 8, padding: '1rem' }}>
              <code style={{ color: '#a5f3fc', fontSize: '.8rem', whiteSpace: 'pre-wrap' }}>
{`# Adicione no crontab da VPS:
# crontab -e

# Rodar alertas todo dia às 8h
0 8 * * * curl -s -X POST \\
  https://sanitech.dnsconsultoria.com/api/communications/alerts/run \\
  -H "Authorization: Bearer SEU_TOKEN_ADMIN"`}
              </code>
            </div>
            <p style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginTop: '.75rem' }}>
              Substitua SEU_TOKEN_ADMIN pelo token gerado no login do admin.
            </p>
          </div>
        </div>
      )}

      {/* SMTP tab */}
      {tab === 'smtp' && (
        <div className="card" style={{ padding: '1.5rem', maxWidth: 600 }}>
          <h3 style={{ margin: '0 0 .5rem', fontSize: '1rem', fontWeight: 600 }}>Configuração SMTP</h3>
          <p style={{ fontSize: '.875rem', color: 'var(--text-muted)', marginBottom: '1.25rem' }}>
            As configurações de e-mail são definidas no arquivo <code>.env</code> da VPS. Edite o arquivo e reinicie o backend:
          </p>
          <div style={{ background: '#1e293b', borderRadius: 8, padding: '1rem', marginBottom: '1rem' }}>
            <code style={{ color: '#a5f3fc', fontSize: '.8rem', whiteSpace: 'pre-wrap' }}>
{`# /sanitech/.env
SMTP_HOST=smtp.gmail.com
SMTP_PORT=587
SMTP_USER=seu@gmail.com
SMTP_PASSWORD=sua-senha-de-app
EMAIL_FROM=seu@gmail.com`}
            </code>
          </div>
          <div style={{ background: '#fef9c3', borderRadius: 8, padding: '1rem', fontSize: '.85rem', color: '#92400e' }}>
            💡 <strong>Dica:</strong> Para Gmail, use uma <em>Senha de App</em> (não a senha normal). Ative a verificação em duas etapas e gere uma senha de app em: Conta Google → Segurança → Senhas de app.
          </div>
        </div>
      )}
    </div>
  );
}
